export class ApiRequestError extends Error {
  readonly status: number;
  // The parsed JSON error body (e.g. { error, code, missingSections }), for callers that need more than the message.
  readonly details: unknown;

  constructor(message: string, status: number, details: unknown = null) {
    super(message);
    this.name = "ApiRequestError";
    this.status = status;
    this.details = details;
  }
}

export function isTransientReadError(error: unknown) {
  if (error instanceof ApiRequestError && error.details && typeof error.details === "object"
    && "code" in error.details && error.details.code === "D1_BUSY") return false;
  return error instanceof TypeError
    || (error instanceof Error && error.name === "TimeoutError")
    || (error instanceof ApiRequestError && [408, 500, 502, 503, 504, 522, 524].includes(error.status));
}

function waitForRetry(delay: number, signal?: AbortSignal | null) {
  return new Promise<void>((resolve, reject) => {
    signal?.throwIfAborted();
    const abort = () => {
      clearTimeout(timer);
      reject(signal?.reason);
    };
    const timer = setTimeout(() => {
      signal?.removeEventListener("abort", abort);
      resolve();
    }, delay);
    signal?.addEventListener("abort", abort, { once: true });
  });
}

// Shared read recovery only; mutations must never be replayed by this helper.
export async function retryTransientRead<T>(
  read: (signal: AbortSignal) => Promise<T>,
  signal?: AbortSignal | null,
  options: {networkOnly?: boolean; timeoutMs?: number} = {},
): Promise<T> {
  const delays = [400, 1000];
  for (let attempt = 0; ; attempt += 1) {
    signal?.throwIfAborted();
    const controller = new AbortController();
    const abort = () => controller.abort(signal?.reason);
    signal?.addEventListener("abort", abort, { once: true });
    const timeoutMs=options.timeoutMs ?? 60_000;
    const timer = timeoutMs>0 ? setTimeout(() => controller.abort(new DOMException("Read timed out", "TimeoutError")), timeoutMs) : undefined;
    let failure: unknown;
    try {
      const result = await read(controller.signal);
      controller.signal.throwIfAborted();
      return result;
    } catch (error) {
      failure = controller.signal.aborted ? controller.signal.reason : error;
    } finally {
      clearTimeout(timer);
      signal?.removeEventListener("abort", abort);
    }
    signal?.throwIfAborted();
    const transient=options.networkOnly ? failure instanceof TypeError : isTransientReadError(failure);
    if (attempt >= delays.length || !transient) throw failure;
    await waitForRetry(delays[attempt] + Math.floor(Math.random() * 250), signal);
  }
}
